import { createStyles } from "@mantine/core";

const SHADOW_INPUT = "0px 1px 2px 0px rgba(16,24,40,0.05)";

// Mantine sizes a Button by a fixed `height` and gives its label
// `height: 100%; overflow: hidden`. These buttons size themselves from padding
// instead, so the label's 100% resolves against the content box and clips the
// text's descenders. Release the label wherever a button is padding sized.
const AUTO_HEIGHT_LABEL = {
  "& .mantine-Button-inner": { height: "auto" },
  "& .mantine-Button-label": {
    height: "auto",
    overflow: "visible",
    lineHeight: 1.45,
  },
};

const DANGER = "#F04438";

const useStyles = createStyles(() => ({
  /* ------------------------------------------------------------------ shell */
  drawer: {
    "& .mantine-Drawer-content": {
      background: "var(--app-bg)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    "& .mantine-Drawer-body": {
      flex: 1,
      minHeight: 0,
      padding: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
  },
  head: {
    flex: "0 0 auto",
    background: "var(--app-surface)",
    borderBottom: "1px solid var(--app-border)",
    padding: "19px 13px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  headTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "var(--app-text)",
    letterSpacing: "-0.02em",
    fontFamily: "Inter",
    "@media (max-width: 600px)": { fontSize: 19 },
  },
  blankBtn: {
    ...AUTO_HEIGHT_LABEL,
    marginLeft: "auto",
    height: 38,
    padding: "0 13px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    boxShadow: SHADOW_INPUT,
    "&:hover": { background: "var(--app-surface-2)" },
  },
  closeBtn: {
    width: 32,
    height: 32,
    minWidth: 32,
    padding: 0,
    borderRadius: 8,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    "&:hover": { background: "var(--app-surface-2)" },
  },
  body: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    background: "var(--app-surface-2)",
    padding: "29px 24px 18px",
    "@media (max-width: 600px)": { padding: "16px 12px" },
  },
  foot: {
    flex: "0 0 auto",
    background: "var(--app-surface)",
    borderTop: "1px solid var(--app-border)",
    padding: "12px 19px 14px",
  },

  /* ------------------------------------------------------------ identity */
  who: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 8,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 13,
    marginBottom: 14,
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    background: "var(--app-tint-amber)",
    border: "2px solid var(--app-primary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "var(--app-text)",
    flex: "0 0 auto",
    overflow: "hidden",
  },
  avatarImage: { width: "100%", height: "100%", objectFit: "cover" },
  whoName: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: "var(--app-text)",
  },
  whoMeta: { fontSize: 12, color: "var(--app-text-muted)", marginTop: 3 },

  /* ---------------------------------------------------------- section card */
  card: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 16,
    padding: "20px 13px 19px",
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--app-text)",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 4,
  },
  badgeRequired: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--app-ink-red)",
    background: "var(--app-tint-red)",
    padding: "3px 6px",
    borderRadius: 5,
  },
  badgeSource: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "var(--app-text-muted)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    padding: "3px 6px",
    borderRadius: 5,
  },
  hint: {
    fontSize: 12,
    color: "var(--app-text-muted)",
    marginBottom: 14,
    lineHeight: 1.45,
  },
  fieldLabel: {
    display: "block",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--app-text)",
    marginBottom: 8,
    "& em": { fontStyle: "normal", color: DANGER, marginLeft: 2 },
  },
  subLabel: {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--app-text-muted)",
    marginBottom: 8,
  },
  errorText: { fontSize: 12, color: DANGER, marginTop: 6, fontWeight: 500 },

  /* -------------------------------------------------------------- inputs */
  input: {
    "& .mantine-Input-wrapper, & .mantine-InputWrapper-root > .mantine-Input-wrapper": {
      height: "auto",
      background: "var(--app-surface)",
      borderRadius: 8,
      border: "1px solid var(--app-border)",
      boxShadow: SHADOW_INPUT,
      "&:focus-within": {
        borderColor: "var(--app-primary)",
        boxShadow: "0 0 0 3px rgba(255,175,74,.2)",
      },
    },
    "& .mantine-Input-input": {
      border: "none",
      background: "transparent",
      padding: "10px 14px",
      height: 44,
      minHeight: 44,
      fontFamily: "Inter",
      fontSize: 14,
      fontWeight: 400,
      color: "var(--app-text)",
    },
    "& .mantine-InputWrapper-label": {
      fontFamily: "Inter",
      fontSize: 14,
      fontWeight: 500,
      color: "var(--app-text)",
      marginBottom: 8,
    },
    "& .mantine-InputWrapper-required": { color: DANGER },
    "& .mantine-Input-rightSection": {
      width: "fit-content",
      paddingRight: 12,
      color: "var(--app-text-muted)",
      fontSize: 12,
    },
  },
  textarea: {
    "& .mantine-Input-input": {
      height: "auto",
      minHeight: 96,
      lineHeight: 1.5,
      padding: "12px 14px",
    },
  },
  inputError: {
    "& .mantine-Input-wrapper": { borderColor: DANGER },
  },
  inputDisabled: {
    "& .mantine-Input-wrapper": { background: "var(--app-surface-2)", opacity: 0.65 },
  },

  /* ------------------------------------------------------- radio buttons */
  radioGroup: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 10,
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  },
  radioTile: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    textAlign: "left",
    cursor: "pointer",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 7,
    padding: "11px 12px",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },
  radioTileActive: {
    borderColor: "var(--app-ink-red)",
    background: "var(--app-tint-red)",
  },
  radioDot: {
    width: 15,
    height: 15,
    borderRadius: "50%",
    border: "1.5px solid var(--app-border)",
    background: "var(--app-surface)",
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  radioDotActive: {
    borderColor: "var(--app-ink-red)",
    background: "var(--app-ink-red)",
    "&::after": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#fff",
    },
  },

  /* ------------------------------------------------------ checkbox tiles */
  checkList: { display: "grid", gap: 2 },
  checkListTwo: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "2px 14px",
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  },
  check: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    cursor: "pointer",
    padding: "7px 0",
  },
  checkBox: {
    width: 18,
    height: 18,
    borderRadius: 5,
    border: "1.5px solid var(--app-border)",
    background: "var(--app-surface)",
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 800,
    color: "transparent",
    marginTop: 1,
  },
  checkBoxActive: {
    background: "var(--app-primary)",
    borderColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
  },
  checkText: {
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1.45,
    color: "var(--app-text)",
    "& em": { fontStyle: "normal", color: DANGER, marginLeft: 2 },
  },
  checkHint: {
    display: "block",
    fontSize: 11.5,
    fontWeight: 400,
    color: "var(--app-text-muted)",
    marginTop: 2,
  },

  /* ----------------------------------------------- read only chart panels */
  chart: {
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    padding: "12px 13px",
    marginBottom: 12,
  },
  chartConflict: { borderColor: "var(--app-ink-red)" },
  chartHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "var(--app-text-muted)",
    marginBottom: 8,
  },
  chartList: {
    listStyle: "none",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "3px 12px",
    margin: 0,
    padding: 0,
    "@media (max-width: 600px)": { gridTemplateColumns: "1fr" },
  },
  chartItem: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--app-text)",
    padding: "1px 0",
    "&::before": {
      content: '""',
      display: "inline-block",
      width: 4,
      height: 4,
      borderRadius: "50%",
      background: "var(--app-text-muted)",
      verticalAlign: 2.5,
      marginRight: 7,
    },
  },
  chartEmpty: {
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    fontStyle: "italic",
  },
  miniTable: {
    width: "100%",
    borderCollapse: "collapse",
    "& th": {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "var(--app-text-muted)",
      textAlign: "left",
      padding: "0 6px 5px 0",
      borderBottom: "1px solid var(--app-border)",
    },
    "& td": {
      fontSize: 12.5,
      color: "var(--app-text)",
      padding: "5px 6px 5px 0",
      borderBottom: "1px solid var(--app-border)",
    },
    "& tr:last-child td": { borderBottom: 0 },
  },
  miniName: { fontWeight: 600 },
  narcotic: {
    fontSize: 9.5,
    fontWeight: 800,
    border: "1px solid var(--app-ink-red)",
    color: "var(--app-ink-red)",
    padding: "0 3px",
    borderRadius: 3,
    marginLeft: 5,
    verticalAlign: 1,
  },

  /* ----------------------------------------------------- warning strips */
  warn: {
    display: "flex",
    gap: 9,
    alignItems: "flex-start",
    background: "var(--app-tint-red)",
    border: "1px solid var(--app-ink-red)",
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 12,
  },
  warnOk: {
    background: "var(--app-tint-green)",
    borderColor: "var(--app-ink-green)",
  },
  warnIcon: {
    width: 17,
    height: 17,
    borderRadius: "50%",
    background: "var(--app-ink-red)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    marginTop: 1,
  },
  warnIconOk: { background: "var(--app-ink-green)" },
  warnText: { fontSize: 12.5, lineHeight: 1.45, color: "var(--app-text)" },

  /* ------------------------------------------------------------- footer */
  gate: {
    fontSize: 12,
    color: "var(--app-ink-red)",
    textAlign: "center",
    marginBottom: 9,
    fontWeight: 500,
  },
  gateDone: { color: "var(--app-text-muted)" },
  actions: { display: "flex", gap: 10 },
  saveBtn: {
    ...AUTO_HEIGHT_LABEL,
    flex: 1,
    height: "auto",
    padding: 12,
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    border: "1px solid var(--app-primary)",
    boxShadow: SHADOW_INPUT,
    "&:hover": { background: "var(--app-primary-hover, #fab005)" },
    "&:disabled": {
      background: "var(--app-primary)",
      color: "var(--app-on-primary)",
      opacity: 0.45,
    },
    "@media (max-width: 600px)": { fontSize: 14 },
  },
  cancelBtn: {
    ...AUTO_HEIGHT_LABEL,
    flex: 1,
    height: "auto",
    padding: 12,
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    background: "var(--app-bg)",
    color: "var(--app-text)",
    border: "1px solid var(--app-border)",
    boxShadow: SHADOW_INPUT,
    "&:hover": { background: "var(--app-surface-2)" },
    "@media (max-width: 600px)": { fontSize: 14 },
  },

  /* ------------------------------------------------------- success view */
  done: { textAlign: "center", padding: "24px 8px 8px" },
  doneIcon: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "var(--app-tint-green)",
    color: "var(--app-ink-green)",
    fontSize: 23,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
  },
  doneTitle: {
    fontSize: 19,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    color: "var(--app-text)",
    marginBottom: 5,
  },
  doneText: {
    fontSize: 13,
    color: "var(--app-text-muted)",
    lineHeight: 1.5,
    maxWidth: 400,
    margin: "0 auto 18px",
  },
  receipt: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 16,
    padding: "16px 15px",
    marginBottom: 14,
    "& table": { width: "100%", borderCollapse: "collapse" },
    "& td": {
      fontSize: 13,
      padding: "6px 0",
      borderBottom: "1px solid var(--app-border)",
      color: "var(--app-text)",
    },
    "& tr:last-child td": { borderBottom: 0 },
    "& td:first-of-type": { color: "var(--app-text-muted)", width: "44%" },
    "& td:last-of-type": { fontWeight: 600, textAlign: "right" },
  },
  doneActions: { display: "grid", gap: 9 },
  doneAction: {
    ...AUTO_HEIGHT_LABEL,
    height: "auto",
    padding: 12,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    background: "var(--app-surface)",
    color: "var(--app-text)",
    border: "1px solid var(--app-border)",
    boxShadow: SHADOW_INPUT,
    "&:hover": { background: "var(--app-surface-2)" },
  },
  doneActionPrimary: {
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    borderColor: "var(--app-primary)",
    "&:hover": { background: "var(--app-primary-hover, #fab005)" },
  },
}));

export default useStyles;
